// Tenant Content Preferences types
// Mirrors backend DEFAULT_PREFERENCES from tenant_preference_service.py

// ── Category sub-types ──────────────────────────────────────────────

export interface WritingStylePreferences {
  formality: number; // 1-5
  humanizer_enabled: boolean;
  ai_detection_avoidance: "off" | "low" | "high";
  sentence_variety: boolean;
  use_questions: boolean;
  use_humor: boolean;
  paragraph_length: "short" | "medium" | "long";
}

export interface TonePreferences {
  primary_tone: string;
  secondary_tone: string;
  custom_tone_description: string | null;
}

export interface VocabularyPreferences {
  avoid_words: string[];
  prefer_phrases: string[];
  reading_level: "simple" | "moderate" | "advanced";
  industry_jargon_level: "low" | "moderate" | "high";
}

export interface ContentStructurePreferences {
  preferred_block_types: string[];
  avoided_block_types: string[];
  faq_preference: "include" | "exclude" | "optional";
  image_preference: "include" | "exclude" | "optional";
  table_preference: "include" | "exclude" | "optional";
  min_sections: number;
  max_sections: number;
  include_key_takeaways: boolean;
}

export interface LinkPreferences {
  max_internal_links: number;
  max_external_links: number;
  preferred_anchor_style: "natural" | "keyword" | "branded";
  link_density: "low" | "moderate" | "high";
  link_review_mode: "fast" | "balanced" | "thorough";
}

export interface BrandMentionDetectedPattern {
  avg_mentions_per_post: number;
  mentions_in_intro_pct: number;
  mentions_in_conclusion_pct: number;
  mentions_in_body_pct: number;
  sample_phrases: string[];
  by_content_type: Record<string, { avg: number; mode: string }>;
}

export interface BrandMentionPreferences {
  mode: "off" | "light" | "strong";
  content_type_overrides: Record<string, string>;
  detected_pattern: BrandMentionDetectedPattern | null;
}

export interface PersonaPreferences {
  locked_persona: string | null;
  excluded_personas: string[];
}

export interface ModelPreferences {
  locked_model: string | null;
  preferred_models: string[];
  excluded_models: string[];
}

export interface NegativeRule {
  id: string;
  rule: string;
  category: string;
  active: boolean;
}

export interface QualityThresholdPreferences {
  min_word_count_override: number | null;
  max_word_count_override: number | null;
  target_content_score: number; // 0-100
  require_featured_image: boolean;
}

export interface UiPreferences {
  content_queue_column_widths?: Record<string, number>;
}

// ── Aggregate preferences ───────────────────────────────────────────

export interface TenantPreferences {
  writing_style: WritingStylePreferences;
  tone: TonePreferences;
  vocabulary: VocabularyPreferences;
  content_structure: ContentStructurePreferences;
  links: LinkPreferences;
  brand_mentions: BrandMentionPreferences;
  persona: PersonaPreferences;
  model: ModelPreferences;
  negative_rules: NegativeRule[];
  quality_thresholds: QualityThresholdPreferences;
  ui_preferences?: UiPreferences;
}

// ── API response types ──────────────────────────────────────────────

export interface PreferencesResponse {
  version: number;
  website: string;
  preferences: TenantPreferences;
}

export interface PreferencesUpdateRequest {
  updates: Partial<TenantPreferences>;
  updated_by: string;
  change_summary?: string;
}

export interface PreferenceHistoryEntry {
  id: string;
  website: string;
  version: number;
  preferences: TenantPreferences;
  changed_fields: string[];
  change_source: "manual" | "rollback";
  change_summary: string;
  changed_by: string;
  created_at: string;
}

export interface RollbackRequest {
  target_version: number;
}

export interface FeedbackPattern {
  id: string;
  tenant_id: string;
  pattern_type: string;
  comment_type: string;
  description: string;
  original_examples: Array<{ text: string }>;
  corrected_examples: Array<{ text: string }>;
  occurrence_count: number;
  confidence_score: number;
  auto_apply_eligible: boolean;
  is_active: boolean;
  created_at: string;
}

export interface GenerationContextResponse {
  writing_style_instructions: string;
  vocabulary_guidance: string;
  negative_rules_text: string;
  custom_tone_instructions: string;
  link_preferences_text: string;
  link_review_mode: string;
  brand_mention_instructions: string;
  locked_persona?: string;
  locked_model?: string;
}

// ── Option constants for form controls ──────────────────────────────

export const PERSONA_OPTIONS = [
  { value: "technical_expert", label: "Technical Expert" },
  { value: "friendly_advisor", label: "Friendly Advisor" },
  { value: "industry_insider", label: "Industry Insider" },
  { value: "consumer_advocate", label: "Consumer Advocate" },
] as const;

export const MODEL_OPTIONS = [
  { value: "anthropic/claude-sonnet-4", label: "Claude Sonnet 4" },
  { value: "anthropic/claude-3.5-sonnet", label: "Claude 3.5 Sonnet" },
  { value: "anthropic/claude-3-opus", label: "Claude 3 Opus" },
  { value: "google/gemini-3-flash-preview", label: "Gemini 3 Flash" },
  { value: "google/gemini-2.5-pro-preview", label: "Gemini 2.5 Pro" },
  { value: "openai/gpt-4o", label: "GPT-4o" },
  { value: "openai/gpt-4o-mini", label: "GPT-4o Mini" },
  { value: "openai/gpt-4-turbo", label: "GPT-4 Turbo" },
] as const;

export const BLOCK_TYPE_OPTIONS = [
  { value: "paragraph", label: "Paragraph" },
  { value: "heading", label: "Heading" },
  { value: "list", label: "List" },
  { value: "table", label: "Table" },
  { value: "faq", label: "FAQ" },
  { value: "image", label: "Image" },
  { value: "blockquote", label: "Blockquote" },
  { value: "callout", label: "Callout" },
  { value: "key_takeaway", label: "Key Takeaway" },
] as const;

export const NEGATIVE_RULE_CATEGORIES = [
  { value: "general", label: "General" },
  { value: "tone", label: "Tone" },
  { value: "content", label: "Content" },
  { value: "formatting", label: "Formatting" },
  { value: "seo", label: "SEO" },
] as const;

export const LINK_REVIEW_MODE_OPTIONS = [
  { value: "fast", label: "Fast (Python only, <100ms)" },
  { value: "balanced", label: "Balanced (Python + LLM for flagged items)" },
  { value: "thorough", label: "Thorough (Full LLM review)" },
] as const;

export const BRAND_MENTION_MODE_OPTIONS = [
  { value: "off", label: "Off - No brand mentions" },
  { value: "light", label: "Light - 1-2 natural mentions" },
  { value: "strong", label: "Strong - Brand woven throughout" },
] as const;

// ── Model Rotation types & defaults ─────────────────────────────────

export interface ModelRotationPreferences {
  rotation_enabled: boolean;
  preferred_models: string[];
  excluded_models: string[];
  rotation_strategy: "round_robin" | "weighted" | "random";
  image_rotation_enabled: boolean;
  image_model_pool: string[];
}

export const MODEL_ROTATION_DEFAULTS: ModelRotationPreferences = {
  rotation_enabled: false,
  preferred_models: [],
  excluded_models: [],
  rotation_strategy: "round_robin",
  image_rotation_enabled: false,
  image_model_pool: [],
};

// ── Image Rotation types & defaults ─────────────────────────────────

export interface ImageRotationPreferences {
  image_rotation_enabled: boolean;
  image_model_pool: string[];
}

export const IMAGE_ROTATION_DEFAULTS: ImageRotationPreferences = {
  image_rotation_enabled: false,
  image_model_pool: [],
};

// ── Image Model Options ─────────────────────────────────────────────

export const IMAGE_MODEL_OPTIONS = [
  { value: "openai/gpt-image-2", label: "GPT Image 2 (fal.ai)" },
  { value: "fal-ai/flux/schnell", label: "FLUX Schnell (fal.ai)" },
  { value: "fal-ai/flux-pro", label: "FLUX Pro (fal.ai)" },
  { value: "fal-ai/flux-realism", label: "FLUX Realism (fal.ai)" },
  { value: "google/imagen", label: "Imagen (Google)" },
] as const;

// ── Generate & Schedule types ───────────────────────────────────────

export interface GenerateScheduleData {
  keyword: string;
  tenant_id: string;
  model_override?: string | null;
  image_model_override?: string | null;
  scheduled_publish_time?: string | null;
  use_model_pool?: boolean;
}

export const GENERATE_SCHEDULE_FIELDS = [
  "keyword",
  "tenant_id",
  "model_override",
  "image_model_override",
  "scheduled_publish_time",
  "use_model_pool",
] as const;
